import { Spin, Typography } from 'antd'
import {
  DataFilterBackground,
  QuantitativeSummaryStatistics,
  useColorScale,
} from 'features/dataLayers'
import { FigureSelectionArea } from 'features/fde/regionTypes'
import {
  FfsStats,
  parseFfsStats,
} from 'features/indicationTriage/customInfoTypes/ffsStats'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useGeckoLocale } from 'features/locale'
import React, { CSSProperties } from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

import { useCroppedCdfSummaryStats } from './useCroppedCdfSummaryStats'

const { Text } = Typography

export interface IndicationThicknessHistogramProps {
  area?: FigureSelectionArea
  refValue?: GeckoMeasurement
  /** Max width of the histogram+markers container. Defaults to '280px'. Pass
   * '100%' to let the graph fill its card (e.g. the libra FFS overview). */
  maxWidth?: string
}

interface HistogramLabelProps {
  measurementRange: GeckoMeasurementRange
  measurement: GeckoMeasurement
  labelHeight: number
  labelPrefix?: string
}

const HistogramLabel: React.FC<HistogramLabelProps> = ({
  measurementRange: measurementRangeRaw,
  measurement,
  labelHeight,
  labelPrefix,
}) => {
  const unit = measurement.unit
  const measurementRange = measurementRangeRaw.convertTo(unit)
  const measurementRangeLen =
    measurementRange.max.num - measurementRange.min.num
  const measurementFrac =
    (measurement.num - measurementRange.min.num) / measurementRangeLen

  const textStyle: CSSProperties = {
    paddingTop: '3px',
    position: 'absolute',
    whiteSpace: 'nowrap',
    top: `100%`,
  }
  const { formatMeasurement } = useGeckoLocale()
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <div
        style={{
          position: 'absolute',
          left: `${measurementFrac * 100}%`,
          top: '0%',
          height: `calc(100% + ${labelHeight ?? 10}px)`,
          borderLeft: 'solid #616161 1px',
        }}>
        <span
          style={{
            ...textStyle,
            ...(measurementFrac <= 0.5
              ? { left: '-0.2rem' }
              : { right: '-0.2rem' }),
          }}>
          <Text strong>{labelPrefix ? `${labelPrefix} ` : ''}</Text>
          <span>{formatMeasurement({ measurement: measurement })}</span>
        </span>
      </div>
    </div>
  )
}

export interface ThicknessSummary {
  min: GeckoMeasurement
  mean: GeckoMeasurement
  max: GeckoMeasurement
}

/** Pick the numeric thickness summary: backend ffsStats (raw-UT, precise) when
 * present, else the cropped CDF stats. Returns null when neither is available. */
export function resolveThicknessSummary(
  ffsStats: FfsStats | null,
  cdfStats: QuantitativeSummaryStatistics | undefined,
  croppedDataAverage: number | undefined,
  cdfUnit: GeckoMeasurementUnit
): ThicknessSummary | null {
  if (ffsStats) {
    return { min: ffsStats.minimum, mean: ffsStats.mean, max: ffsStats.maximum }
  }
  if (!cdfStats) return null
  return {
    min: cdfStats.value_range.min,
    max: cdfStats.value_range.max,
    mean: GeckoMeasurement.CreateFromValue(
      croppedDataAverage ?? cdfStats.mean.num,
      cdfUnit
    ),
  }
}

/**
 * A component to show the thickness distribution within a specificied area,
 * along with min- and average- markers.
 */
export const IndicationThicknessHistogram: React.FC<
  IndicationThicknessHistogramProps
> = (props) => {
  const {
    colorScaleThreshold: selectedColorThreshold,
    relevantThicknessCDF: cdf,
    componentDataFrames,
    indication,
  } = useIndicationData()
  const { area: selection } = props

  const {
    stats: cdfStats,
    isLoading: cdfStatsLoading,
    croppedCdf,
    croppedDataAverage,
  } = useCroppedCdfSummaryStats(cdf, selection)

  const { colorScale } = useColorScale(croppedCdf, selectedColorThreshold)

  const fullRange =
    cdfStats?.value_range.convertTo(
      cdf?.data.params.measurement_unit ?? GeckoMeasurementUnit.Millimeters
    ) ??
    GeckoMeasurementRange.CreateFromValues(0, 1, GeckoMeasurementUnit.Inches)

  const { getPreferredMeasurementUnit, formatMeasurement } = useGeckoLocale()
  const cdfUnit = croppedCdf?.data.params.measurement_unit ?? fullRange.unit
  const preferredMeasUnit = getPreferredMeasurementUnit(cdfUnit)

  const ffsStats = parseFfsStats(indication?.customInfo?.ffsStats)
  const canPlotDistribution = !!cdfStats?.histogram?.length
  const thicknessSummary = resolveThicknessSummary(
    ffsStats,
    cdfStats,
    croppedDataAverage,
    cdfUnit
  )

  const renderThicknessSummaryValue = (value: GeckoMeasurement | undefined) =>
    value
      ? formatMeasurement({ measurement: value.convertTo(preferredMeasUnit) })
      : '—'

  return (
    <Spin spinning={cdfStatsLoading}>
      <div
        style={{
          margin: 0,
          padding: 0,
          maxWidth: props.maxWidth ?? '280px',
          position: 'relative',
        }}>
        {!cdfStats ? null : canPlotDistribution ? (
          <>
            <DataFilterBackground
              cdfId={croppedCdf?.id ?? componentDataFrames[0].id}
              valueRange={cdfStats.value_range}
              histogram={cdfStats.histogram}
              fullValueRange={fullRange}
              filteredValueRange={fullRange}
              selectedColorScale={colorScale}
            />
            <HistogramLabel
              measurement={fullRange.min.convertTo(preferredMeasUnit)}
              measurementRange={fullRange}
              labelHeight={30}
              labelPrefix='Min'
            />
            <HistogramLabel
              measurement={GeckoMeasurement.CreateFromValue(
                croppedDataAverage ?? 0,
                cdfUnit
              ).convertTo(preferredMeasUnit)}
              measurementRange={fullRange}
              labelHeight={10}
              labelPrefix='Avg'
            />
            {props.refValue && (
              <HistogramLabel
                measurement={props.refValue.convertTo(preferredMeasUnit)}
                measurementRange={fullRange}
                labelHeight={50}
                labelPrefix='Reference Value'
              />
            )}
          </>
        ) : (
          <div style={{ marginBottom: '0.5rem' }}>
            <Text type='secondary'>
              This indication is smaller than one scan-resolution bin, so a
              thickness distribution can’t be plotted. Summary values below are
              from the FFS analysis.
            </Text>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
              <span>
                <Text strong>Min </Text>
                {renderThicknessSummaryValue(thicknessSummary?.min)}
              </span>
              <span>
                <Text strong>Mean </Text>
                {renderThicknessSummaryValue(thicknessSummary?.mean)}
              </span>
              <span>
                <Text strong>Max </Text>
                {renderThicknessSummaryValue(thicknessSummary?.max)}
              </span>
            </div>
          </div>
        )}
      </div>
      {canPlotDistribution && <div style={{ height: 'calc(30px + 2.7rem)' }} />}
    </Spin>
  )
}
